/**
 * Pharmacy Defaults — Single Source of Truth
 *
 * Centralized configuration for all pharmacy-type-specific values.
 * Replaces 38+ hardcoded constants scattered across connectors,
 * tools, analyzers, and API clients.
 *
 * Usage:
 *   import { getPharmacyDefaults } from '@fey/core/pharmacy-defaults'
 *   const defaults = getPharmacyDefaults(branch.pharmacyType)
 *   const threshold = item.threshold ?? defaults.defaultThreshold
 */

import type { PharmacyType, SupplyChainRole } from '../types'

// ── Interface ────────────────────────────────────────────────

export interface PharmacyDefaults {
  /** Human-readable label */
  label: string
  /** Short description for UI */
  description: string
  /** Whether this type buys only, or buys and sells */
  supplyChainRole: SupplyChainRole

  // ── Stock Thresholds ──
  /** Default reorder point when item has no threshold set */
  defaultThreshold: number
  /** Fallback floor used in secondary calculations */
  secondaryThreshold: number
  /** Minimum value for smart threshold calculation */
  smartThresholdFloor: number
  /** Ratio of category median used for smart thresholds */
  smartThresholdRatio: number

  // ── Procurement Quantities ──
  /** How many days of supply to order */
  targetDaysSupply: number
  /** Safety buffer multiplier on order quantities */
  safetyStockMultiplier: number
  /** Minimum units per order line */
  minOrderQuantity: number

  // ── Stock Recovery ──
  /** Multiplier when recovering from stockout (order threshold * this) */
  stockoutRecoveryMultiplier: number
  /** Multiplier when recovering from deficit (order deficit * this) */
  deficitRecoveryMultiplier: number

  // ── Classification Thresholds ──
  /** Quantity > threshold * this = overstocked */
  overstockedMultiplier: number
  /** Quantity > threshold * this + no recent sales = dead stock */
  deadStockMultiplier: number

  // ── Time Horizons ──
  /** Days ahead to predict stockouts */
  stockoutPredictionDays: number
  /** Flag items expiring within this many days */
  expiryAlertDays: number
  /** Clearance window — items close to expiry needing action */
  expiryClearanceDays: number

  // ── Urgency Tiers ──
  /** Quantity <= threshold * this ratio = critical */
  criticalThresholdRatio: number
  /** Days until stockout <= this = critical urgency */
  criticalDaysUrgency: number
  /** Days until stockout <= this = high urgency */
  highDaysUrgency: number
}

// ── Defaults Per Pharmacy Type ───────────────────────────────

export const PHARMACY_DEFAULTS: Record<PharmacyType, PharmacyDefaults> = {
  community: {
    label: 'Community Pharmacy',
    description: 'Retail pharmacy serving walk-in patients',
    supplyChainRole: 'buyer',
    defaultThreshold: 20,
    secondaryThreshold: 10,
    smartThresholdFloor: 10,
    smartThresholdRatio: 0.3,
    targetDaysSupply: 14,
    safetyStockMultiplier: 1.2,
    minOrderQuantity: 10,
    stockoutRecoveryMultiplier: 2,
    deficitRecoveryMultiplier: 1.5,
    overstockedMultiplier: 3,
    deadStockMultiplier: 2,
    stockoutPredictionDays: 14,
    expiryAlertDays: 90,
    expiryClearanceDays: 60,
    criticalThresholdRatio: 0.1,
    criticalDaysUrgency: 3,
    highDaysUrgency: 7,
  },

  hospital: {
    label: 'Hospital Pharmacy',
    description: 'Institutional pharmacy within a hospital or health system',
    supplyChainRole: 'buyer',
    defaultThreshold: 50,
    secondaryThreshold: 25,
    smartThresholdFloor: 25,
    smartThresholdRatio: 0.25,
    targetDaysSupply: 21,
    safetyStockMultiplier: 1.3,
    minOrderQuantity: 25,
    stockoutRecoveryMultiplier: 2.5,
    deficitRecoveryMultiplier: 1.5,
    overstockedMultiplier: 4,
    deadStockMultiplier: 3,
    stockoutPredictionDays: 21,
    expiryAlertDays: 120,
    expiryClearanceDays: 90,
    criticalThresholdRatio: 0.15,
    criticalDaysUrgency: 2,
    highDaysUrgency: 5,
  },

  wholesale: {
    label: 'Wholesale / Distribution',
    description: 'Pharmaceutical wholesaler or distributor',
    supplyChainRole: 'buyer_and_seller',
    defaultThreshold: 100,
    secondaryThreshold: 50,
    smartThresholdFloor: 50,
    smartThresholdRatio: 0.2,
    targetDaysSupply: 30,
    safetyStockMultiplier: 1.15,
    minOrderQuantity: 50,
    stockoutRecoveryMultiplier: 2,
    deficitRecoveryMultiplier: 1.3,
    overstockedMultiplier: 5,
    deadStockMultiplier: 3,
    stockoutPredictionDays: 30,
    expiryAlertDays: 180,
    expiryClearanceDays: 120,
    criticalThresholdRatio: 0.1,
    criticalDaysUrgency: 5,
    highDaysUrgency: 10,
  },

  clinic: {
    label: 'Clinic Dispensary',
    description: 'Small pharmacy within a medical practice or clinic',
    supplyChainRole: 'buyer',
    defaultThreshold: 15,
    secondaryThreshold: 5,
    smartThresholdFloor: 5,
    smartThresholdRatio: 0.3,
    targetDaysSupply: 14,
    safetyStockMultiplier: 1.2,
    minOrderQuantity: 5,
    stockoutRecoveryMultiplier: 2,
    deficitRecoveryMultiplier: 1.5,
    overstockedMultiplier: 3,
    deadStockMultiplier: 2,
    stockoutPredictionDays: 14,
    expiryAlertDays: 90,
    expiryClearanceDays: 60,
    criticalThresholdRatio: 0.15,
    criticalDaysUrgency: 2,
    highDaysUrgency: 5,
  },

  ppmv: {
    label: 'Patent Medicine Vendor',
    description: 'Over-the-counter medicine vendor (Nigeria PPMV)',
    supplyChainRole: 'buyer',
    defaultThreshold: 10,
    secondaryThreshold: 5,
    smartThresholdFloor: 5,
    smartThresholdRatio: 0.35,
    targetDaysSupply: 7,
    safetyStockMultiplier: 1.1,
    minOrderQuantity: 5,
    stockoutRecoveryMultiplier: 2,
    deficitRecoveryMultiplier: 1.5,
    overstockedMultiplier: 3,
    deadStockMultiplier: 2,
    stockoutPredictionDays: 7,
    expiryAlertDays: 60,
    expiryClearanceDays: 30,
    criticalThresholdRatio: 0.2,
    criticalDaysUrgency: 2,
    highDaysUrgency: 4,
  },
}

// ── Validation ───────────────────────────────────────────────

const VALID_PHARMACY_TYPES = new Set<string>(['community', 'hospital', 'wholesale', 'clinic', 'ppmv'])

/** Runtime type guard — validates a string from DB or external input */
export function isValidPharmacyType(value: unknown): value is PharmacyType {
  return typeof value === 'string' && VALID_PHARMACY_TYPES.has(value)
}

/** Parse a DB value into PharmacyType, returning undefined for invalid values */
export function parsePharmacyType(value: unknown): PharmacyType | undefined {
  return isValidPharmacyType(value) ? value : undefined
}

// ── Accessor Functions ───────────────────────────────────────

/** Get defaults for a pharmacy type, falling back to community */
export function getPharmacyDefaults(type?: PharmacyType | null): PharmacyDefaults {
  return PHARMACY_DEFAULTS[type || 'community']
}

/** Get the supply chain role for a pharmacy type */
export function getSupplyChainRole(type?: PharmacyType | null): SupplyChainRole {
  return getPharmacyDefaults(type).supplyChainRole
}

/** Whether this pharmacy type can also act as a supplier */
export function isSupplierType(type?: PharmacyType | null): boolean {
  return getSupplyChainRole(type) === 'buyer_and_seller'
}

// ── UI Options ───────────────────────────────────────────────

export const PHARMACY_TYPE_OPTIONS: ReadonlyArray<{
  id: PharmacyType
  label: string
  description: string
  icon: string
}> = [
  { id: 'community', label: 'Community Pharmacy', description: 'Retail pharmacy serving walk-in patients', icon: '🏪' },
  { id: 'hospital', label: 'Hospital Pharmacy', description: 'Institutional pharmacy within a hospital', icon: '🏥' },
  { id: 'wholesale', label: 'Wholesale / Distribution', description: 'Pharmaceutical wholesaler or distributor', icon: '🏭' },
  { id: 'clinic', label: 'Clinic Dispensary', description: 'Small pharmacy within a medical practice', icon: '⚕️' },
  { id: 'ppmv', label: 'Patent Medicine Vendor', description: 'OTC medicine vendor (Nigeria)', icon: '💊' },
]

// ── Per-Branch Overrides ─────────────────────────────────────

/** The 17 numeric fields from PharmacyDefaults that can be overridden per-branch */
export interface OperationalOverrides {
  defaultThreshold: number
  secondaryThreshold: number
  smartThresholdFloor: number
  smartThresholdRatio: number
  targetDaysSupply: number
  safetyStockMultiplier: number
  minOrderQuantity: number
  stockoutRecoveryMultiplier: number
  deficitRecoveryMultiplier: number
  overstockedMultiplier: number
  deadStockMultiplier: number
  stockoutPredictionDays: number
  expiryAlertDays: number
  expiryClearanceDays: number
  criticalThresholdRatio: number
  criticalDaysUrgency: number
  highDaysUrgency: number
}

export const OPERATIONAL_OVERRIDE_KEYS: ReadonlyArray<keyof OperationalOverrides> = [
  'defaultThreshold', 'secondaryThreshold', 'smartThresholdFloor', 'smartThresholdRatio',
  'targetDaysSupply', 'safetyStockMultiplier', 'minOrderQuantity',
  'stockoutRecoveryMultiplier', 'deficitRecoveryMultiplier',
  'overstockedMultiplier', 'deadStockMultiplier',
  'stockoutPredictionDays', 'expiryAlertDays', 'expiryClearanceDays',
  'criticalThresholdRatio', 'criticalDaysUrgency', 'highDaysUrgency',
] as const

/** UI metadata for each overridable parameter */
export const OPERATIONAL_PARAM_META: Record<keyof OperationalOverrides, {
  label: string
  description: string
  unit: string
  group: 'core' | 'advanced'
}> = {
  defaultThreshold: { label: 'Default reorder point', description: 'Reorder point for items with no threshold set', unit: 'units', group: 'core' },
  targetDaysSupply: { label: 'Target days supply', description: 'How many days of supply to order', unit: 'days', group: 'core' },
  expiryAlertDays: { label: 'Expiry alert window', description: 'Flag items expiring within this many days', unit: 'days', group: 'core' },
  minOrderQuantity: { label: 'Minimum order quantity', description: 'Minimum units per order line', unit: 'units', group: 'core' },
  safetyStockMultiplier: { label: 'Safety stock multiplier', description: 'Safety buffer multiplier on order quantities', unit: 'x', group: 'core' },
  secondaryThreshold: { label: 'Secondary threshold', description: 'Fallback floor in secondary calculations', unit: 'units', group: 'advanced' },
  smartThresholdFloor: { label: 'Smart threshold floor', description: 'Minimum for smart threshold calculation', unit: 'units', group: 'advanced' },
  smartThresholdRatio: { label: 'Smart threshold ratio', description: 'Ratio of category median for smart thresholds', unit: 'ratio', group: 'advanced' },
  stockoutRecoveryMultiplier: { label: 'Stockout recovery multiplier', description: 'Multiplier when recovering from stockout', unit: 'x', group: 'advanced' },
  deficitRecoveryMultiplier: { label: 'Deficit recovery multiplier', description: 'Multiplier when recovering from deficit', unit: 'x', group: 'advanced' },
  overstockedMultiplier: { label: 'Overstocked threshold', description: 'qty > threshold * this = overstocked', unit: 'x', group: 'advanced' },
  deadStockMultiplier: { label: 'Dead stock threshold', description: 'qty > threshold * this + no sales = dead', unit: 'x', group: 'advanced' },
  stockoutPredictionDays: { label: 'Stockout prediction horizon', description: 'Days ahead to predict stockouts', unit: 'days', group: 'advanced' },
  expiryClearanceDays: { label: 'Expiry clearance window', description: 'Clearance window for near-expiry items', unit: 'days', group: 'advanced' },
  criticalThresholdRatio: { label: 'Critical threshold ratio', description: 'qty <= threshold * this = critical', unit: 'ratio', group: 'advanced' },
  criticalDaysUrgency: { label: 'Critical urgency days', description: 'Days until stockout <= this = critical', unit: 'days', group: 'advanced' },
  highDaysUrgency: { label: 'High urgency days', description: 'Days until stockout <= this = high urgency', unit: 'days', group: 'advanced' },
}

/** Validation bounds for each overridable parameter */
export const OPERATIONAL_PARAM_BOUNDS: Record<keyof OperationalOverrides, { min: number; max: number; isInteger?: boolean }> = {
  defaultThreshold: { min: 1, max: 10000, isInteger: true },
  secondaryThreshold: { min: 0, max: 10000, isInteger: true },
  smartThresholdFloor: { min: 0, max: 10000, isInteger: true },
  smartThresholdRatio: { min: 0.01, max: 1 },
  targetDaysSupply: { min: 1, max: 365, isInteger: true },
  safetyStockMultiplier: { min: 0.5, max: 5 },
  minOrderQuantity: { min: 1, max: 10000, isInteger: true },
  stockoutRecoveryMultiplier: { min: 1, max: 10 },
  deficitRecoveryMultiplier: { min: 1, max: 10 },
  overstockedMultiplier: { min: 1.5, max: 20 },
  deadStockMultiplier: { min: 1, max: 20 },
  stockoutPredictionDays: { min: 1, max: 365, isInteger: true },
  expiryAlertDays: { min: 7, max: 365, isInteger: true },
  expiryClearanceDays: { min: 7, max: 365, isInteger: true },
  criticalThresholdRatio: { min: 0.01, max: 0.5 },
  criticalDaysUrgency: { min: 1, max: 30, isInteger: true },
  highDaysUrgency: { min: 1, max: 60, isInteger: true },
}

/** DB column name → TS field name mapping */
const DB_TO_OVERRIDE_MAP: Record<string, keyof OperationalOverrides> = {
  default_threshold: 'defaultThreshold',
  secondary_threshold: 'secondaryThreshold',
  smart_threshold_floor: 'smartThresholdFloor',
  smart_threshold_ratio: 'smartThresholdRatio',
  target_days_supply: 'targetDaysSupply',
  safety_stock_multiplier: 'safetyStockMultiplier',
  min_order_quantity: 'minOrderQuantity',
  stockout_recovery_multiplier: 'stockoutRecoveryMultiplier',
  deficit_recovery_multiplier: 'deficitRecoveryMultiplier',
  overstocked_multiplier: 'overstockedMultiplier',
  dead_stock_multiplier: 'deadStockMultiplier',
  stockout_prediction_days: 'stockoutPredictionDays',
  expiry_alert_days: 'expiryAlertDays',
  expiry_clearance_days: 'expiryClearanceDays',
  critical_threshold_ratio: 'criticalThresholdRatio',
  critical_days_urgency: 'criticalDaysUrgency',
  high_days_urgency: 'highDaysUrgency',
}

/** TS field name → DB column name mapping */
export const OVERRIDE_TO_DB_MAP: Record<keyof OperationalOverrides, string> = Object.fromEntries(
  Object.entries(DB_TO_OVERRIDE_MAP).map(([db, ts]) => [ts, db])
) as Record<keyof OperationalOverrides, string>

/** Map a DB row (snake_case, nullable) to camelCase OperationalOverrides, stripping nulls */
export function mapDbToOverrides(row: Record<string, unknown>): Partial<OperationalOverrides> | null {
  const result: Partial<OperationalOverrides> = {}
  let hasAny = false
  for (const [dbKey, tsKey] of Object.entries(DB_TO_OVERRIDE_MAP)) {
    const val = row[dbKey]
    if (val != null && typeof val === 'number' && Number.isFinite(val)) {
      ;(result as any)[tsKey] = val
      hasAny = true
    }
  }
  return hasAny ? result : null
}

/** Map camelCase overrides to snake_case DB columns for upsert */
export function overridesToDb(overrides: Partial<OperationalOverrides>): Record<string, number | null> {
  const result: Record<string, number | null> = {}
  for (const key of OPERATIONAL_OVERRIDE_KEYS) {
    const val = overrides[key]
    const dbKey = OVERRIDE_TO_DB_MAP[key]
    result[dbKey] = val != null && Number.isFinite(val) ? val : null
  }
  return result
}

/**
 * Resolve pharmacy defaults with optional per-branch overrides.
 * null/undefined override values fall back to the type default.
 */
export function resolvePharmacyDefaults(
  pharmacyType?: PharmacyType | null,
  overrides?: Partial<OperationalOverrides> | null,
): PharmacyDefaults {
  const base = getPharmacyDefaults(pharmacyType)
  if (!overrides) return base
  const resolved = { ...base }
  for (const key of OPERATIONAL_OVERRIDE_KEYS) {
    const val = overrides[key]
    if (val != null && Number.isFinite(val)) {
      ;(resolved as any)[key] = val
    }
  }
  return resolved
}
